/**
 * <ry-button-group>
 *
 * Segmented control / button group. Groups child buttons with
 * connected borders and optional radio-group behavior.
 *
 * Usage (visual grouping only):
 * <ry-button-group>
 *   <ry-button>Left</ry-button>
 *   <ry-button>Center</ry-button>
 *   <ry-button>Right</ry-button>
 * </ry-button-group>
 *
 * Usage (radio behavior — single selection):
 * <ry-button-group name="mode" value="terminal">
 *   <ry-button value="direct">Direct</ry-button>
 *   <ry-button value="terminal">Terminal</ry-button>
 *   <ry-button value="release">Release</ry-button>
 * </ry-button-group>
 *
 * Emits ry:change with { value } when selection changes.
 */

import { RyElement } from '../core/ry-element.js';

export class RyButtonGroup extends RyElement {
  static get observedAttributes(): string[] {
    return ['value'];
  }

  get value(): string {
    return this.getAttribute('value') ?? '';
  }

  set value(v: string) {
    this.setAttribute('value', v);
  }

  setup(): void {
    this.setAttribute('role', 'group');
    this.#syncPressed();
    this.on(this, 'click', this.#handleClick);
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (name === 'value' && oldValue !== newValue) {
      this.#syncPressed();
    }
  }

  #handleClick = (e: MouseEvent): void => {
    const target = (e.target as HTMLElement).closest<HTMLElement>('ry-button, ry-toggle-button');
    if (!target || !this.contains(target)) return;

    const val = target.getAttribute('value');
    if (val == null) return;

    if (this.value !== val) {
      this.value = val;
      this.emit('change', { value: val });
    }
  };

  #syncPressed(): void {
    const current = this.value;
    if (!current) return;

    for (const btn of this.querySelectorAll<HTMLElement>('ry-button, ry-toggle-button')) {
      if (btn.getAttribute('value') === current) {
        btn.setAttribute('pressed', '');
        btn.setAttribute('aria-pressed', 'true');
      } else {
        btn.removeAttribute('pressed');
        btn.removeAttribute('aria-pressed');
      }
    }
  }
}

customElements.define('ry-button-group', RyButtonGroup);
